/**
 * TechFloorPlan — Technician view of the interactive floor plan for a service.
 * Shows the client's plans, devices, and lets the tech tap or scan QR to inspect.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, QrCode, Map as MapIcon, CheckCircle2, PackageOpen, ZoomIn, ZoomOut, Square, MapPin, X, Check, PlusCircle } from "lucide-react";
import FloorPlanCanvas from "@/components/FloorPlanCanvas";
import DeviceInspectionSheet from "@/components/DeviceInspectionSheet";
import DeviceInstallSheet from "@/components/DeviceInstallSheet";
import AreaFindingSheet from "@/components/AreaFindingSheet";
import AreaCreateSheet from "@/components/AreaCreateSheet";
import QrScannerModal from "@/components/QrScannerModal";
import { findingToStatus } from "@/lib/inspectionCatalog";

export default function TechFloorPlan() {
    const { id: serviceId } = useParams();
    const nav = useNavigate();
    const _loc = useLocation();
    const basePath = _loc.pathname.startsWith("/audit") ? "/audit" : "/tech";
    const [service, setService] = useState(null);
    const [plans, setPlans] = useState([]);
    const [activePlan, setActivePlan] = useState(null);
    const [devices, setDevices] = useState([]);
    const [inspections, setInspections] = useState([]);
    const [allAreaFindings, setAllAreaFindings] = useState([]); // ALL findings for client's plans (history)
    const [installMode, setInstallMode] = useState(false);
    const [editDevice, setEditDevice] = useState(null);
    const [installDevice, setInstallDevice] = useState(null);
    const [editArea, setEditArea] = useState(null);
    const [showScanner, setShowScanner] = useState(false);
    const [highlightId, setHighlightId] = useState(null);
    const [zoom, setZoom] = useState(1.5);
    const [draftArea, setDraftArea] = useState(null); // {x,y,w,h} in % when drafting
    const [pendingRect, setPendingRect] = useState(null);
    // Relocation flow
    const [relocateState, setRelocateState] = useState(null);
    // Device request flow
    const [requestMode, setRequestMode] = useState(false);
    const [requestPos, setRequestPos] = useState(null);
    const [showRequestDialog, setShowRequestDialog] = useState(false);
    const [reqForm, setReqForm] = useState({ device_type: "cebadero_cebo", zone: "verde", trap_subtype: "caja", comment: "" });
    // Device add flow
    const [addMode, setAddMode] = useState(false);
    const [addPos, setAddPos] = useState(null);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [addForm, setAddForm] = useState({ device_type: "cebadero_cebo", zone: "verde", trap_subtype: "caja", comment: "" });

    const startAreaDraft = () => {
        // Place a default 30% × 25% rect roughly centered
        setDraftArea({ x: 35, y: 35, w: 30, h: 25 });
    };
    const cancelAreaDraft = () => setDraftArea(null);
    const confirmAreaDraft = () => {
        if (draftArea) {
            setPendingRect(draftArea);
            setDraftArea(null);
        }
    };

    const ZOOM_MIN = 0.5;
    const ZOOM_MAX = 4;
    const ZOOM_STEP = 0.25;
    const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
    const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
    const zoomReset = () => setZoom(1);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/schedules/${serviceId}`);
                setService(r.data);
                const p = await api.get(`/floor-plans?client_id=${r.data.client_id}`);
                setPlans(p.data);
                if (p.data[0]) setActivePlan(p.data[0]);
                const d = await api.get(`/pest-devices?client_id=${r.data.client_id}`);
                setDevices(d.data);
                const i = await api.get(`/device-inspections?service_id=${serviceId}`);
                setInspections(i.data);
                // Fetch ALL area findings for this client's plans (history across services)
                if (p.data[0]) {
                    const af = await api.get(`/area-findings?floor_plan_id=${p.data[0].id}`);
                    setAllAreaFindings(af.data);
                }
                // Auto-suggest install mode when nothing is installed yet for this client
                const anyInstalled = d.data.some((x) => x.installation_date);
                if (!anyInstalled && d.data.length > 0) {
                    setInstallMode(true);
                    toast.info("Modo instalación activado: aún no hay dispositivos instalados");
                }
            } catch (e) {
                toast.error("No se pudo cargar el mapa");
            }
        })();
    }, [serviceId]);

    const refreshInspections = async () => {
        try {
            const i = await api.get(`/device-inspections?service_id=${serviceId}`);
            setInspections(i.data);
        } catch (e) { /* ignore */ }
    };

    const refreshAreaFindings = async () => {
        if (!activePlan) return;
        try {
            const af = await api.get(`/area-findings?floor_plan_id=${activePlan.id}`);
            setAllAreaFindings(af.data);
        } catch (e) { /* ignore */ }
    };

    // Compute LATEST area finding per area_id (latest = most recent created_at)
    const areaStatusByAreaId = useMemo(() => {
        const latestByArea = {};
        for (const f of allAreaFindings) {
            const prev = latestByArea[f.area_id];
            if (!prev || (f.created_at || "") > (prev.created_at || "")) {
                latestByArea[f.area_id] = f;
            }
        }
        const out = {};
        for (const [aid, f] of Object.entries(latestByArea)) {
            out[aid] = f.risk_level === "alto" ? "alert" : f.risk_level === "medio" ? "warning" : "ok";
        }
        return out;
    }, [allAreaFindings]);

    const refreshDevices = async () => {
        if (!service) return;
        try {
            const d = await api.get(`/pest-devices?client_id=${service.client_id}`);
            setDevices(d.data);
        } catch (e) { /* ignore */ }
    };

    const statusByDeviceId = useMemo(() => {
        const map = {};
        const deviceById = {};
        for (const d of devices) deviceById[d.id] = d;
        for (const ins of inspections) {
            const dev = deviceById[ins.device_id];
            // Prefer new finding_code-driven status; fall back to legacy fields
            let status = null;
            if (ins.finding_code && dev) {
                status = findingToStatus(dev.device_type, dev.trap_subtype, ins.finding_code);
            }
            if (!status) {
                const high = ins.activity_level === "high" ||
                             ins.trap_status === "damaged" || ins.trap_status === "triggered" ||
                             ins.lamp_status === "needs_replacement";
                const med = ins.activity_level === "medium" || ins.activity_level === "low" ||
                            ins.lamp_status === "needs_cleaning" ||
                            ins.bait_consumed === true;
                status = high ? "alert" : med ? "warning" : "ok";
            }
            map[ins.device_id] = status;
        }
        return map;
    }, [inspections, devices]);

    const planDevices = useMemo(() => {
        if (!activePlan) return [];
        return devices.filter((d) => d.floor_plan_id === activePlan.id);
    }, [devices, activePlan]);

    const inspectedCount = planDevices.filter((d) => statusByDeviceId[d.id]).length;
    const installedCount = planDevices.filter((d) => d.installation_date).length;
    const progressPct = planDevices.length ? Math.round((installedCount / planDevices.length) * 100) : 0;
    const allInstalled = planDevices.length > 0 && installedCount === planDevices.length;

    const openForDevice = (dev, fromScan = false) => {
        setHighlightId(dev.id);
        // Pending QR devices: offer to relocate
        if (dev.qr_status === "pending" && !fromScan) {
            toast.info(`${dev.device_number} — Pendiente de QR`, {
                action: {
                    label: "Mover",
                    onClick: () => startRelocate(dev),
                },
                duration: 5000,
            });
            setTimeout(() => setHighlightId((id) => id === dev.id ? null : id), 5000);
            return;
        }
        if (installMode) {
            if (!fromScan && !dev.installation_date) {
                toast.error(`Escanea el QR físico de ${dev.device_number} para marcarlo como instalado.`, {
                    duration: 4500,
                });
                setTimeout(() => setHighlightId((id) => id === dev.id ? null : id), 5000);
                return;
            }
            setInstallDevice(dev);
        } else if (!dev.installation_date) {
            toast.error(`${dev.device_number} aún no está instalado. Actívalo en Modo instalación primero.`, {
                action: {
                    label: "Activar modo",
                    onClick: () => setInstallMode(true),
                },
                duration: 5000,
            });
        } else {
            setEditDevice(dev);
        }
        setTimeout(() => setHighlightId((id) => id === dev.id ? null : id), 5000);
    };

    // --- Relocation flow ---
    const startRelocate = (dev) => {
        setEditDevice(null); // close sheet
        setRelocateState({ device: dev, x: dev.position_x, y: dev.position_y });
        toast.info(`Toca la nueva ubicación de ${dev.device_number} en el plano`, { duration: 4000 });
    };
    const cancelRelocate = () => setRelocateState(null);
    const handleRelocateMapTap = (x, y) => {
        if (!relocateState) return;
        setRelocateState((s) => ({ ...s, x, y }));
    };
    const confirmRelocate = async () => {
        if (!relocateState) return;
        const { device, x, y } = relocateState;
        if (x === device.position_x && y === device.position_y) {
            toast.error("No has movido el dispositivo. Toca una nueva ubicación o cancela.");
            return;
        }
        try {
            const r = await api.post(`/pest-devices/${device.id}/relocate`, { position_x: x, position_y: y });
            setDevices((ds) => ds.map((d) => (d.id === device.id ? { ...d, ...r.data } : d)));
            toast.success(`${device.device_number} reubicado`);
            setRelocateState(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo reubicar");
        }
    };

    // --- Device add flow (tech adds directly, QR pending) ---
    const startAddMode = () => {
        setRequestMode(false); setRequestPos(null);
        setAddMode(true); setAddPos(null);
        toast.info("Toca en el plano donde quieres colocar el dispositivo", { duration: 4000 });
    };
    const handleAddMapTap = (x, y) => {
        if (!addMode) return;
        setAddPos({ x, y });
        setShowAddDialog(true);
    };
    const submitAddDevice = async () => {
        if (!addPos || !activePlan || !service) return;
        try {
            const res = await api.post("/pest-devices", {
                client_id: service.client_id,
                floor_plan_id: activePlan.id,
                device_type: addForm.device_type,
                zone: addForm.zone,
                trap_subtype: addForm.trap_subtype || "",
                position_x: addPos.x,
                position_y: addPos.y,
                qr_status: "pending",
            });
            await api.post("/device-requests", {
                floor_plan_id: activePlan.id,
                client_id: service.client_id,
                device_type: addForm.device_type,
                zone: addForm.zone,
                trap_subtype: addForm.trap_subtype || "",
                position_x: addPos.x,
                position_y: addPos.y,
                comment: addForm.comment || `Dispositivo ${res.data?.device_number || ""} agregado por técnico — pendiente de QR`,
                device_id: res.data?.id,
                request_type: "added",
            });
            toast.success(`${res.data?.device_number || "Dispositivo"} agregado — pendiente de QR`);
            setShowAddDialog(false);
            setAddMode(false);
            setAddPos(null);
            setAddForm({ device_type: "cebadero_cebo", zone: "verde", trap_subtype: "caja", comment: "" });
            await refreshDevices();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al agregar dispositivo");
        }
    };

    // --- Device request flow (sends request/alert to admin) ---
    const startRequestMode = () => {
        setAddMode(false); setAddPos(null);
        setRequestMode(true); setRequestPos(null);
        toast.info("Toca en el plano donde quieres solicitar el dispositivo", { duration: 4000 });
    };
    const handleRequestMapTap = (x, y) => {
        if (!requestMode) return;
        setRequestPos({ x, y });
        setShowRequestDialog(true);
    };
    const submitDeviceRequest = async () => {
        if (!requestPos || !activePlan || !service) return;
        try {
            await api.post("/device-requests", {
                floor_plan_id: activePlan.id,
                client_id: service.client_id,
                device_type: reqForm.device_type,
                zone: reqForm.zone,
                trap_subtype: reqForm.trap_subtype || "",
                position_x: requestPos.x,
                position_y: requestPos.y,
                comment: reqForm.comment,
            });
            toast.success("Solicitud enviada al administrador");
            setShowRequestDialog(false);
            setRequestMode(false);
            setRequestPos(null);
            setReqForm({ device_type: "cebadero_cebo", zone: "verde", trap_subtype: "caja", comment: "" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al enviar solicitud");
        }
    };
    const DEVICE_TYPES = [
        { v: "cebadero_cebo", l: "Cebadero" },
        { v: "trampa", l: "Trampa captura" },
        { v: "lampara_uv", l: "Lámpara UV" },
    ];
    const ZONES = [
        { v: "verde", l: "Verde (perímetro exterior)" },
        { v: "amarilla", l: "Amarilla (puntos focales interiores)" },
        { v: "roja", l: "Roja (áreas de acceso al cliente)" },
    ];
    const BAIT_SUBS = [
        { v: "caja", l: "Cebadero de caja" },
        { v: "tubo", l: "Cebadero de tubo" },
        { v: "roca", l: "Cebadero roca" },
    ];
    const TRAP_SUBS = [
        { v: "gato_papel", l: "Gato papel" },
        { v: "bandeja", l: "Bandeja" },
        { v: "multicaptura", l: "Multicaptura" },
        { v: "trex", l: "T-Rex" },
    ];


    const handleQrScanned = (qrCode) => {
        setShowScanner(false);
        const local = devices.find((d) => d.qr_code === qrCode);
        if (local) {
            // Switch plan if needed
            const plan = plans.find((p) => p.id === local.floor_plan_id);
            if (plan && (!activePlan || activePlan.id !== plan.id)) {
                setActivePlan(plan);
                // small delay so the new plan renders before highlighting
                setTimeout(() => openForDevice(local, true), 200);
            } else {
                openForDevice(local, true);
            }
            return;
        }
        api.get(`/pest-devices/by-qr/${encodeURIComponent(qrCode)}`)
            .then((r) => {
                if (r.data?.client_id === service?.client_id) {
                    setDevices((ds) => [...ds, r.data]);
                    setTimeout(() => openForDevice(r.data, true), 200);
                } else {
                    toast.error("QR no pertenece a este cliente");
                }
            })
            .catch(() => toast.error(`QR no registrado: ${qrCode}`));
    };

    if (!service) {
        return (
            <div className="max-w-2xl mx-auto p-4">
                <p className="text-sm text-muted-foreground">Cargando...</p>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto pb-24" data-testid="tech-floor-plan">
            <div className="px-4 pt-4">
                <Link to={`${basePath}/service/${serviceId}`} className="inline-flex items-center text-sm text-muted-foreground mb-3 hover:text-foreground" data-testid="tech-fp-back">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Volver al servicio
                </Link>
                <div className="flex items-center justify-between mb-3">
                    <h1 className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        <MapIcon className="inline-block w-5 h-5 mr-1.5 -mt-0.5" />
                        Mapa interactivo
                    </h1>
                    <div className="text-xs text-muted-foreground">
                        {installMode ? (
                            <span className="inline-flex items-center gap-1">
                                <PackageOpen className="w-3.5 h-3.5 text-orange-600" /> {installedCount}/{planDevices.length} instalados
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> {inspectedCount}/{planDevices.length}
                            </span>
                        )}
                    </div>
                </div>

                {/* Install mode toggle */}
                <Card className="p-3 mb-3 flex items-center justify-between" data-testid="install-mode-card">
                    <div>
                        <Label htmlFor="install-mode-toggle" className="text-sm font-medium flex items-center gap-1.5">
                            <PackageOpen className="w-4 h-4 text-orange-600" /> Modo instalación
                        </Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            {installMode
                                ? "Escanea el QR físico de cada dispositivo para registrarlo."
                                : "Activa para guiarte en la instalación inicial del perímetro."}
                        </p>
                    </div>
                    <Switch
                        id="install-mode-toggle"
                        checked={installMode}
                        onCheckedChange={setInstallMode}
                        data-testid="install-mode-toggle"
                    />
                </Card>

                {/* Progress bar in install mode */}
                {installMode && planDevices.length > 0 && (
                    <div className="mb-3" data-testid="install-progress">
                        <div className="w-full h-2 bg-slate-200 rounded overflow-hidden">
                            <div
                                className="h-full bg-green-600 transition-all duration-300"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 flex justify-between">
                            <span>{installedCount} instalados</span>
                            <span>{planDevices.length - installedCount} pendientes</span>
                        </div>
                    </div>
                )}

                {/* All done banner */}
                {installMode && allInstalled && (
                    <Card className="mb-3 p-3 bg-green-50 border-green-300 text-green-800 text-sm flex items-center gap-2" data-testid="install-complete-banner">
                        <CheckCircle2 className="w-5 h-5" />
                        <div>
                            ¡Perímetro completo! Puedes desactivar Modo instalación para continuar con inspecciones.
                        </div>
                    </Card>
                )}

                {/* Plan tabs (if multiple) */}
                {plans.length > 1 && (
                    <Tabs value={activePlan?.id || ""} onValueChange={(v) => setActivePlan(plans.find((p) => p.id === v))}>
                        <TabsList className="w-full overflow-x-auto justify-start">
                            {plans.map((p) => (
                                <TabsTrigger key={p.id} value={p.id} data-testid={`tech-plan-tab-${p.id}`}>
                                    {p.name}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                )}
                {plans.length === 1 && activePlan && (
                    <div className="text-sm font-medium text-foreground mb-1">{activePlan.name}</div>
                )}

                {/* Horizontal zoom controls + area button under plan name */}
                {activePlan && (
                    <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid="tech-zoom-controls">
                        <button
                            onClick={zoomOut}
                            className="p-1.5 border rounded hover:bg-slate-100 active:bg-slate-200"
                            title="Alejar"
                            data-testid="tech-zoom-out"
                        >
                            <ZoomOut className="w-4 h-4" />
                        </button>
                        <button
                            onClick={zoomReset}
                            className="px-2 py-1 border rounded text-xs font-mono hover:bg-slate-100 min-w-[52px]"
                            title="Tamaño original"
                            data-testid="tech-zoom-reset"
                        >
                            {Math.round(zoom * 100)}%
                        </button>
                        <button
                            onClick={zoomIn}
                            className="p-1.5 border rounded hover:bg-slate-100 active:bg-slate-200"
                            title="Acercar"
                            data-testid="tech-zoom-in"
                        >
                            <ZoomIn className="w-4 h-4" />
                        </button>
                        <button
                            onClick={draftArea ? cancelAreaDraft : startAreaDraft}
                            className={`ml-2 px-2 py-1.5 border rounded text-xs flex items-center gap-1 ${draftArea ? "bg-blue-600 text-white border-blue-600" : "hover:bg-slate-100"}`}
                            title="Nombrar área"
                            data-testid="tech-name-area-btn"
                        >
                            <Square className="w-3.5 h-3.5" /> {draftArea ? "Cancelar" : "Nombrar área"}
                        </button>
                        <button
                            onClick={requestMode ? () => { setRequestMode(false); setRequestPos(null); } : startRequestMode}
                            className={`px-2 py-1.5 border rounded text-xs flex items-center gap-1 ${requestMode ? "bg-amber-600 text-white border-amber-600" : "hover:bg-slate-100"}`}
                            title="Solicitar dispositivo"
                            data-testid="tech-request-device-btn"
                        >
                            <PlusCircle className="w-3.5 h-3.5" /> {requestMode ? "Cancelar" : "Solicitar"}
                        </button>
                        <button
                            onClick={addMode ? () => { setAddMode(false); setAddPos(null); } : startAddMode}
                            className={`px-2 py-1.5 border rounded text-xs flex items-center gap-1 ${addMode ? "bg-orange-600 text-white border-orange-600" : "hover:bg-slate-100"}`}
                            title="Agregar dispositivo"
                            data-testid="tech-add-device-btn"
                        >
                            <PlusCircle className="w-3.5 h-3.5" /> {addMode ? "Cancelar" : "Agregar"}
                        </button>
                    </div>
                )}
                {draftArea && (
                    <div className="flex justify-end mt-2">
                        <button
                            onClick={confirmAreaDraft}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700"
                            data-testid="tech-confirm-area-draft"
                        >
                            Continuar →
                        </button>
                    </div>
                )}
            </div>

            {/* Empty state */}
            {plans.length === 0 && (
                <Card className="m-4 p-6 text-center text-muted-foreground">
                    Este cliente aún no tiene planos cargados.
                </Card>
            )}

            {/* Canvas */}
            {activePlan && (
                <Card className="my-3 p-0 overflow-hidden" data-testid="tech-fp-card">
                    {relocateState && (
                        <div className="bg-blue-50 border border-blue-300 rounded-md p-2.5 mb-2 flex items-center gap-2" data-testid="relocate-banner">
                            <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
                            <span className="text-xs text-blue-900 flex-1">
                                <b>Reubicando {relocateState.device.device_number}:</b> toca la nueva ubicación en el plano.
                            </span>
                        </div>
                    )}
                    <FloorPlanCanvas
                        imageUrl={activePlan.image_url}
                        devices={planDevices.map((d) =>
                            relocateState && d.id === relocateState.device.id
                                ? { ...d, position_x: relocateState.x, position_y: relocateState.y }
                                : d
                        )}
                        areas={activePlan.areas || []}
                        mode={relocateState ? "view" : "edit-devices"}
                        statusByDeviceId={statusByDeviceId}
                        areaStatusByAreaId={areaStatusByAreaId}
                        installMode={installMode}
                        highlightedDeviceId={relocateState ? relocateState.device.id : highlightId}
                        showLabels={true}
                        controlledZoom={zoom}
                        onZoomChange={setZoom}
                        hideZoomControls={true}
                        draftArea={draftArea}
                        onDraftAreaChange={setDraftArea}
                        onDraftAreaConfirm={confirmAreaDraft}
                        onMapClick={relocateState ? handleRelocateMapTap : requestMode ? handleRequestMapTap : addMode ? handleAddMapTap : undefined}
                        onDeviceClick={(d) => {
                            if (relocateState) return; // ignore device taps during relocation
                            openForDevice(d);
                        }}
                        onAreaClick={(a) => {
                            if (relocateState) return;
                            setEditArea(a);
                        }}
                        className="rounded overflow-hidden"
                    />
                </Card>
            )}

            {/* Legend */}
            <div className="mx-4 mb-20 text-[11px] text-muted-foreground space-y-1">
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <span className="font-semibold">Color base (zona):</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-600 mr-1 align-middle" />Verde</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500 mr-1 align-middle" />Amarilla</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-600 mr-1 align-middle" />Roja</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-purple-600 mr-1 align-middle" />UV</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <span className="font-semibold">Inspección:</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle ring-2 ring-emerald-700" style={{ background: "transparent" }} />OK</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle ring-2 ring-orange-500" style={{ background: "transparent" }} />Atención</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle ring-2 ring-red-900" style={{ background: "transparent" }} />Alerta</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300 border border-dashed border-gray-500 mr-1 align-middle" />Pendiente de instalar</span>
                    {installMode && <span>✓ verde = instalado</span>}
                    <span>Badge de esquina = estado de inspección</span>
                </div>
            </div>

            {/* Floating QR scan button (hidden during relocation) */}
            {!relocateState && (
                <Button
                    onClick={() => setShowScanner(true)}
                    className="fixed bottom-20 right-5 z-[60] rounded-full w-14 h-14 p-0 shadow-lg bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black"
                    data-testid="tech-fp-scan-qr"
                >
                    <QrCode className="w-6 h-6" />
                </Button>
            )}

            {/* Relocate confirm bar */}
            {relocateState && (
                <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white border-t shadow-lg p-3 flex gap-2" data-testid="relocate-confirm-bar">
                    <Button
                        variant="outline"
                        onClick={cancelRelocate}
                        className="flex-1"
                        data-testid="relocate-cancel"
                    >
                        <X className="w-4 h-4 mr-1" /> Cancelar
                    </Button>
                    <Button
                        onClick={confirmRelocate}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="relocate-confirm"
                    >
                        <Check className="w-4 h-4 mr-1" /> Guardar nueva ubicación
                    </Button>
                </div>
            )}

            {/* Sheets & Scanner */}
            <DeviceInspectionSheet
                open={!!editDevice}
                device={editDevice}
                serviceId={serviceId}
                onClose={() => setEditDevice(null)}
                onSaved={refreshInspections}
                onRelocate={startRelocate}
            />
            <DeviceInstallSheet
                open={!!installDevice}
                device={installDevice}
                onClose={() => setInstallDevice(null)}
                onUpdated={refreshDevices}
            />
            <AreaFindingSheet
                open={!!editArea}
                area={editArea}
                floorPlanId={activePlan?.id}
                serviceId={serviceId}
                allFindings={allAreaFindings}
                onClose={() => setEditArea(null)}
                onSaved={refreshAreaFindings}
            />
            <AreaCreateSheet
                open={!!pendingRect}
                rect={pendingRect}
                floorPlanId={activePlan?.id}
                serviceId={serviceId}
                onClose={() => setPendingRect(null)}
                onSaved={(updatedPlan) => {
                    setPlans((ps) => ps.map((p) => p.id === updatedPlan.id ? updatedPlan : p));
                    setActivePlan(updatedPlan);
                    refreshAreaFindings();
                }}
            />
            <QrScannerModal
                open={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleQrScanned}
            />

            {/* Device Request Dialog (solicitud al admin) */}
            <Dialog open={showRequestDialog} onOpenChange={(v) => { if (!v) { setShowRequestDialog(false); } }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <PlusCircle className="w-5 h-5 text-amber-600" /> Solicitar dispositivo
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Tipo de dispositivo</Label>
                            <Select value={reqForm.device_type} onValueChange={(v) => setReqForm(f => ({ ...f, device_type: v, trap_subtype: v === "cebadero_cebo" ? "caja" : v === "trampa" ? "gato_papel" : "" }))}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{DEVICE_TYPES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Zona</Label>
                            <Select value={reqForm.zone} onValueChange={(v) => setReqForm(f => ({ ...f, zone: v }))}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{ZONES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        {reqForm.device_type !== "lampara_uv" && (
                            <div>
                                <Label className="text-xs">Subtipo</Label>
                                <Select value={reqForm.trap_subtype} onValueChange={(v) => setReqForm(f => ({ ...f, trap_subtype: v }))}>
                                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(reqForm.device_type === "cebadero_cebo" ? BAIT_SUBS : TRAP_SUBS).map(o => (
                                            <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Comentario</Label>
                            <Input value={reqForm.comment} onChange={(e) => setReqForm(f => ({ ...f, comment: e.target.value }))} placeholder="Ej: Se necesita cebadero cerca de la puerta trasera" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancelar</Button>
                        <Button onClick={submitDeviceRequest} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tech-request-submit">
                            Enviar solicitud
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Device Add Dialog (agrega directo, pendiente de QR) */}
            <Dialog open={showAddDialog} onOpenChange={(v) => { if (!v) { setShowAddDialog(false); } }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <PlusCircle className="w-5 h-5 text-orange-600" /> Agregar dispositivo
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground -mt-1">El dispositivo se creará de inmediato. El QR se asignará después.</p>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Tipo de dispositivo</Label>
                            <Select value={addForm.device_type} onValueChange={(v) => setAddForm(f => ({ ...f, device_type: v, trap_subtype: v === "cebadero_cebo" ? "caja" : v === "trampa" ? "gato_papel" : "" }))}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{DEVICE_TYPES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Zona</Label>
                            <Select value={addForm.zone} onValueChange={(v) => setAddForm(f => ({ ...f, zone: v }))}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{ZONES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        {addForm.device_type !== "lampara_uv" && (
                            <div>
                                <Label className="text-xs">Subtipo</Label>
                                <Select value={addForm.trap_subtype} onValueChange={(v) => setAddForm(f => ({ ...f, trap_subtype: v }))}>
                                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(addForm.device_type === "cebadero_cebo" ? BAIT_SUBS : TRAP_SUBS).map(o => (
                                            <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs">Comentario (opcional)</Label>
                        <Input value={addForm.comment} onChange={(e) => setAddForm(f => ({ ...f, comment: e.target.value }))} placeholder="Ej: Colocado junto a la puerta trasera" data-testid="tech-add-comment" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancelar</Button>
                        <Button onClick={submitAddDevice} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="tech-add-submit">
                            Agregar dispositivo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
